import { isDefined, isNotDefined } from '@sgme/fp';
import { Tooltip } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgAmountInput } from '@/components/sg-design-system/components/input/SgAmountInput';
import { getConfig } from '@/config/config.ts';
import { useAppDispatch } from '@/store/hooks.ts';
import {
  getQuote,
  selectOptionCurrency,
  selectOptionHedge,
  selectQuoteHedge,
} from '@/store/option/selectors/optionSelectors.ts';
import { optionSlice } from '@/store/option/slice.ts';
import type { FxOption, LegId } from '@/store/option/state.ts';

type DeltaHedgeProps = {
  tileId: string;
};

export function DeltaHedge({ tileId }: DeltaHedgeProps) {
  const [checkOption] = useCheckOption();
  const dispatch = useAppDispatch();

  const legId: LegId = `${tileId}/0`;
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { hedgeAmount, hedgeCurrency, rfsHedgeAmount, hedgeCurrencyIndex, hedgeType, hedgeLegId, residualDelta } =
    useOptionSelector((state) => selectDeltaHedgeData(state, legId));

  const isPinned = isDefined(hedgeAmount);

  function handleChange(hedgeAmount: number | undefined | null) {
    sgwtWebAnalytics?.trackEvent('Option', 'Delta Hedge amount changed', String(hedgeAmount));

    if (isNotDefined(hedgeLegId)) {
      return;
    }

    // This works around a bug in trade capture where, sometimes, the hedge amount is not returned by trade capture after being set
    // We set it to the right value optimistically, and then if trade capture does not return the value correctly, it will be set to undefined again
    dispatch(
      optionSlice.actions.optimisticUpdateHedgeAmount({ tileId, hedgeLegId, hedgeAmount: hedgeAmount ?? undefined }),
    );
    return checkOption({
      tileId,
      hedges: {
        legId: hedgeLegId,
        notionalAmount: hedgeAmount,
        notionalCurrency: hedgeCurrencyIndex,
      },
    });
  }

  function toggleAmountCurrency() {
    const newCurrencyIndex = hedgeCurrencyIndex === 1 ? 2 : 1;
    if (isNotDefined(hedgeLegId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Option', 'Delta Hedge Currency toggled', String(newCurrencyIndex));
    checkOption({
      tileId,
      hedges: {
        legId: hedgeLegId,
        notionalAmount: hedgeAmount,
        notionalCurrency: newCurrencyIndex,
      },
    });
  }

  if (hedgeType === 'Live') {
    return null;
  }

  const aboveRecommendHedgeAmount = isAboveRecommendHedgeAmount(hedgeAmount, residualDelta);
  const displayedHedgeValue = hedgeAmount ?? rfsHedgeAmount;
  const input = (
    <SgAmountInput
      data-e2e="deltaHedge"
      size="sm"
      onUnitClick={toggleAmountCurrency}
      unit={hedgeCurrency}
      value={displayedHedgeValue}
      onChange={handleChange}
      className={isPinned ? 'text-info fw-bold' : undefined}
      unitBackgroundColor={hedgeCurrencyIndex === 2 ? 'warning' : undefined}
      state={aboveRecommendHedgeAmount ? 'warning' : 'default'}
    />
  );

  return (
    <OptionFormGroup
      label={<FormattedMessage id="option.fields.deltaHedge" />}
      addon={
        isPinned && (
          <button
            data-e2e="delta-hedge-pin"
            onClick={() => handleChange(null)}
            type="button"
            className="btn btn-sm btn-icon btn-flat-primary"
          >
            <em className="icon icon-sm cursor-default line-height-1 text-secondary">push_pin</em>
          </button>
        )
      }
    >
      {aboveRecommendHedgeAmount ? (
        <Tooltip color="warning" position="right" contents={<FormattedMessage id="option.fields.hedgeAmount.alert" />}>
          <div>{input}</div>
        </Tooltip>
      ) : (
        input
      )}
    </OptionFormGroup>
  );
}

function selectDeltaHedgeData(option: FxOption, legId: LegId) {
  const rfsHedge = selectQuoteHedge(option, legId);
  const hedge = selectOptionHedge(option, legId);
  const { currency1, currency2 } = selectOptionCurrency(option);

  const currencyIndex = hedge?.notionalCurrency ?? 1;
  const notionalAmount = hedge?.notionalAmount;
  const currencyLabel = currencyIndex === 1 ? currency1 : currency2;

  const quote = getQuote(option);
  const residualDelta = currencyIndex === 1 ? quote?.ccy1.deltaCcy : quote?.ccy2.deltaCcy;

  return {
    residualDelta,
    rfsHedgeAmount: rfsHedge?.notionalAmount,
    rfsHedgeCurrency: rfsHedge?.notionalCurrency,
    hedgeLegId: hedge?.legId,

    hedgeAmount: notionalAmount,
    hedgeCurrency: currencyLabel,
    hedgeCurrencyIndex: currencyIndex,

    hedgeType: option?.properties.hedgeType,
  };
}

function isAboveRecommendHedgeAmount(hedgeAmount: number | undefined, residualDelta: number | undefined) {
  if (isNotDefined(hedgeAmount) || isNotDefined(residualDelta)) {
    return false;
  }

  const totalDelta = hedgeAmount + residualDelta;
  const percentResidualDelta = Math.abs(residualDelta / totalDelta);
  const recommendedHedgeAmountInPercent = getConfig().app.recommendedHedgeAmountInPercent;

  return percentResidualDelta > recommendedHedgeAmountInPercent;
}
